import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/solid'; // Using Heroicons for the back arrow
import MinimalLayout from '../components/layout/MinimalLayout';

// Base options - wording will be adapted in the rendering logic for guardians
const ageOptions = ['18 and above', '13 to 17', '10 to 12', '8 or 9', '7 and below'];
const baseGoalOptions = [
  'I want to learn to play solely for my own enjoyment.',
  'I want to practice regularly and perform for others occasionally.',
  'I have or want a schedule of performances that I am always working on, but I don\'t currently aspire to a career in music.',
  'I am committed to playing at the highest level, including pursuing a career in music.'
];
const baseSkillLevelOptions = [
  { title: 'I haven\'t started learning yet.', description: '' },
  { title: 'Beginner', description: 'I just started playing with basic technical understanding.' },
  { title: 'Intermediate', description: 'I have a fair technical understanding and am exploring creative expression.' },
  { title: 'Advanced', description: 'I have instrument mastery but am seeking stronger individual expression.' }
];
const genreOptions = [
  // Most commonly used genres first
  'Pop', 'Rock', 'Classical', 'Jazz', 'Blues', 'Country', 'Folk', 'R&B', 
  'Hip Hop', 'Alternative', 'Indie', 'Metal', 'Electronic', 'EDM', 'Reggae', 
  'Soul', 'Funk', 'Punk', 'Gospel', 'Worship', 'Latin', 'Musical Theater',
  
  // Moderately used genres
  'Rap', 'K-pop', 'Disco', 'House', 'Techno', 'Grunge', 'Emo', 'World',
  'Bluegrass', 'Americana', 'Irish', 'Ambient', 'Lo-fi', 'New-Age',
  
  // Less commonly used genres
  'Flamenco', 'Post-rock', 'Trance', 'Ska', 'Synth-pop', 'Indian classical', 
  'Baroque', 'Honky tonk', 'Cajun', 'Mariachi', 'Other'
];
const previousLessonsOptions = ['No', 'Yes - in person', 'Yes - online', 'Yes - online and in person'];
const lessonFrequencyOptions = [
  'Once per week - I want to stay focused and motivated, while making consistent progress.',
  'Every other week - I\'m good at retaining information and can practice on my own between lessons.'
];
const lessonLocationOptions = ['In my home', "At the teacher's studio", 'Online'];

interface QuestionnaireData {
  instrument: string | null;
  learnerType: 'learner' | 'guardian' | null;
  learnerFirstName: string | null;
  age: string | null;
  goals: string | null;
  skillLevel: string | null;
  genres: string[];
  previousLessons: string | null;
  lessonFrequency: string | null;
  lessonLocation: string[];
}

type InputFieldKey = 'learnerFirstName';
type SingleSelectFieldKey = keyof Pick<QuestionnaireData, 'age' | 'goals' | 'skillLevel' | 'previousLessons' | 'lessonFrequency'>;

const LearnerQuestionnairePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialLearnerType = params.get('learnerType') as 'learner' | 'guardian' | null;
  const isGuardian = initialLearnerType === 'guardian';

  const TOTAL_QUESTIONS = isGuardian ? 9 : 8;

  const [currentQuestionStep, setCurrentQuestionStep] = useState(1);
  const [formData, setFormData] = useState<QuestionnaireData>(() => ({
    instrument: params.get('instrument') || 'music',
    learnerType: initialLearnerType,
    learnerFirstName: null,
    age: null,
    goals: null,
    skillLevel: null,
    genres: [],
    previousLessons: null,
    lessonFrequency: null,
    lessonLocation: [],
  }));

  useEffect(() => {
    console.log(`Questionnaire Step ${currentQuestionStep}/${TOTAL_QUESTIONS}`, { isGuardian, formData });
  }, [currentQuestionStep, formData, isGuardian, TOTAL_QUESTIONS]);

  const handleNextStep = () => {
    // Guardian name validation
    if (isGuardian && currentQuestionStep === 1 && (!formData.learnerFirstName || formData.learnerFirstName.trim() === '')) {
      alert('Please enter the learner\'s first name.');
      return;
    }

    // Genre validation - require at least one genre
    const guardianQuestionOffset = isGuardian ? 1 : 0;
    const currentActualQuestionIndex = currentQuestionStep - guardianQuestionOffset;
    
    if (currentActualQuestionIndex === 4 && formData.genres.length === 0) {
      alert('Please select at least one music genre you\'re interested in.');
      return;
    }

    // Validation for lessonLocation before navigating on the final step
    if (currentQuestionStep === TOTAL_QUESTIONS) { // This is when the "Create Account" button is effectively clicked
      if (formData.lessonLocation.length === 0) {
        const learnerNameForAlert = formData.learnerFirstName || (isGuardian ? 'the learner' : 'you');
        alert(adaptTextForGuardian(`Please select at least one preferred lesson location for ${learnerNameForAlert}.`, learnerNameForAlert, true)); // Pass true for isNameProvided
        return;
      }
      console.log('Questionnaire complete:', formData);
      navigate('/account-creation', { state: { questionnaireData: formData } });
      return; // Ensure we don't try to increment step after navigating
    }

    if (currentQuestionStep < TOTAL_QUESTIONS) {
      setCurrentQuestionStep(prev => prev + 1);
    } 
    // Removed the else block that navigated, as it's handled above
  };

  const handlePreviousStep = () => {
    if (currentQuestionStep > 1) {
      setCurrentQuestionStep(prev => prev - 1);
    }
  };

  const handleInputChange = (field: InputFieldKey, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSingleSelect = (field: SingleSelectFieldKey, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    const guardianQuestionOffset = isGuardian ? 1 : 0;
    const currentActualQuestionIndex = currentQuestionStep - guardianQuestionOffset;

    if ( (field === 'age' && currentActualQuestionIndex === 1) || 
         (field === 'goals' && currentActualQuestionIndex === 2) || 
         (field === 'skillLevel' && currentActualQuestionIndex === 3) ) {
      setTimeout(handleNextStep, 250); // This will correctly go to next step, or trigger validation if it's the last meaningful question before account creation.
    }
  };

  const handleGenreToggle = (genre: string) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre],
    }));
  };

  const handleLocationToggle = (location: string) => {
    setFormData(prev => ({
      ...prev,
      lessonLocation: prev.lessonLocation.includes(location)
        ? prev.lessonLocation.filter(l => l !== location)
        : [...prev.lessonLocation, location],
    }));
  };

  const ProgressBar = () => (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
        style={{ width: `${(currentQuestionStep / TOTAL_QUESTIONS) * 100}%` }}
      ></div>
    </div>
  );
  
  const adaptTextForGuardian = (text: string, learnerName: string, isNameProvided: boolean = false) => {
    if (!isGuardian) return text;
    const nameToUse = isNameProvided ? learnerName : (formData.learnerFirstName || 'the learner');
    return text.replace(/I am/g, 'They are')
               .replace(/I have/g, 'They have')
               .replace(/I want/g, 'They want')
               .replace(/I\'m/g, 'They\'re')
               .replace(/my own/g, 'their own')
               .replace(/myself/g, 'themselves')
               .replace(/you are looking/g, `is ${nameToUse} looking`)
               .replace(/Where are you looking/g, `Where is ${nameToUse} looking`)
               .replace(/for you\./g, `for ${nameToUse}.`); // For the alert message
  };

  const renderQuestion = () => {
    const instrumentName = formData.instrument ? formData.instrument.toLowerCase() : 'the instrument';
    const learnerName = formData.learnerFirstName || (isGuardian ? 'the learner' : 'you'); // Simplified for titles
    
    let questionContentStep = currentQuestionStep;
    if (isGuardian) {
      if (currentQuestionStep === 1) {
        return (
          <QuestionLayout title="What is the learner's first name?" showContinueButton={!!(formData.learnerFirstName && formData.learnerFirstName.trim())} onContinue={handleNextStep}>
            <input
              type="text" placeholder="Learner's first name"
              value={formData.learnerFirstName || ''}
              onChange={(e) => handleInputChange('learnerFirstName', e.target.value)}
              className="w-full px-5 py-3.5 bg-white text-gray-800 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder-gray-500 text-base shadow-sm"
              autoFocus
            />
          </QuestionLayout>
        );
      }
      questionContentStep = currentQuestionStep - 1;
    }

    switch (questionContentStep) {
      case 1: // Age
        return (
          <QuestionLayout title={isGuardian ? `How old is ${learnerName}?` : "How old are you?"}>
            {ageOptions.map(option => (
              <OptionButton key={option} text={option} field="age" selectedValue={formData.age} onSelect={handleSingleSelect} />
            ))}
          </QuestionLayout>
        );
      case 2: // Goals
        return (
          <QuestionLayout title={isGuardian ? `What are ${learnerName}\'s goals for learning ${instrumentName}?` : `What are your goals for learning ${instrumentName}?`}>
            {baseGoalOptions.map(optionText => {
              const adaptedText = adaptTextForGuardian(optionText, learnerName);
              return <OptionButton key={adaptedText} text={adaptedText} field="goals" selectedValue={formData.goals} onSelect={handleSingleSelect} />;
            })}
          </QuestionLayout>
        );
      case 3: // Skill Level
        return (
          <QuestionLayout title={isGuardian ? `What is ${learnerName}\'s current skill level on ${instrumentName}?` : `What is your current skill level on ${instrumentName}?`}>
            {baseSkillLevelOptions.map(option => {
              const adaptedTitle = adaptTextForGuardian(option.title, learnerName);
              const adaptedDescription = adaptTextForGuardian(option.description, learnerName);
              return <SkillLevelButton key={adaptedTitle} title={adaptedTitle} description={adaptedDescription} field="skillLevel" selectedValue={formData.skillLevel} onSelect={handleSingleSelect} />;
            })}
          </QuestionLayout>
        );
      case 4: // Genres
        return (
          <QuestionLayout title={`Which genre(s) ${isGuardian ? 'is ' + learnerName : 'are you'} interested in learning for ${instrumentName}?`} showContinueButton={formData.genres.length > 0} onContinue={handleNextStep}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
              {genreOptions.map(genre => (
                <GenreButton key={genre} genre={genre} selectedGenres={formData.genres} onToggle={handleGenreToggle} />
              ))}
            </div>
          </QuestionLayout>
        );
      case 5: // Previous Lessons
        return (
          <QuestionLayout title={isGuardian ? `Has ${learnerName} taken music lessons before?` : "Have you taken music lessons before?"} showContinueButton={formData.previousLessons !== null} onContinue={handleNextStep}>
            {previousLessonsOptions.map(option => {
              const adaptedText = adaptTextForGuardian(option, learnerName); // Ensure this is adapted if necessary
              return <OptionButton key={option} text={adaptedText} field="previousLessons" selectedValue={formData.previousLessons} onSelect={handleSingleSelect} />;
            })}
          </QuestionLayout>
        );
      case 6: // Lesson Frequency
        return (
          <QuestionLayout title={isGuardian ? `How often does ${learnerName} plan to take lessons?` : "How often do you plan to take lessons?"} showContinueButton={formData.lessonFrequency !== null} onContinue={handleNextStep}>
            {lessonFrequencyOptions.map(optionText => {
                const adaptedText = adaptTextForGuardian(optionText, learnerName);
                return <OptionButton key={adaptedText} text={adaptedText} field="lessonFrequency" selectedValue={formData.lessonFrequency} onSelect={handleSingleSelect} />;
            })}
            <SkipButton onClick={handleNextStep} />
          </QuestionLayout>
        );
      case 7: // Lesson Location - Updated title and ensure adaptTextForGuardian is used correctly
        return (
          <QuestionLayout 
            title={adaptTextForGuardian("Where are you looking for lessons?", learnerName, true)} 
            showContinueButton={formData.lessonLocation.length > 0} 
            onContinue={handleNextStep}
          >
            <div className="space-y-3 max-w-md mx-auto">
              {lessonLocationOptions.map(option => (
                <label key={option} className={`flex items-center p-3 border rounded-lg cursor-pointer ${formData.lessonLocation.includes(option) ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-100'}`}>
                  <input type="checkbox" checked={formData.lessonLocation.includes(option)} onChange={() => handleLocationToggle(option)} className="form-checkbox h-5 w-5 text-blue-600 rounded mr-3" />
                  {option} {/* Options are generic, no need to adapt them */}
                </label>
              ))}
            </div>
          </QuestionLayout>
        );
      case 8: // Final Step - Create Account
        return (
          <div className="text-center pt-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-800 mb-6">
              {adaptTextForGuardian("Last step: Create an account to browse teachers for you.", learnerName, true)}
            </h2>
            <button onClick={handleNextStep} className="w-full max-w-md mx-auto text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-semibold rounded-full text-lg px-8 py-4 mb-4 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all">
              Create Account
            </button>
            <p className="text-sm text-gray-500">
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        );
      default:
        return <p>Error: Unknown question step.</p>;
    }
  };

  // Layout for each question screen
  const QuestionLayout: React.FC<{ title: string; children: React.ReactNode; showContinueButton?: boolean; onContinue?: () => void; }> = 
  ({ title, children, showContinueButton = false, onContinue }) => (
    <div className="flex flex-col h-full">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6 sm:mb-8 text-center">{title}</h2>
        <div className="mb-6 sm:mb-8">
          {children}
        </div>
      </div>
      {showContinueButton && onContinue && (
        <button 
          onClick={onContinue} 
          className="w-full mt-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out text-base sm:text-lg"
        >
          Continue
        </button>
      )}
    </div>
  );

  // Standard option button
  const OptionButton: React.FC<{text: string, field: SingleSelectFieldKey, selectedValue: string | null, onSelect: (field: SingleSelectFieldKey, value: string) => void }> = 
    ({ text, field, selectedValue, onSelect }) => {
    const isSelected = selectedValue === text;
    return (
        <button 
            onClick={() => onSelect(field, text)} 
            className={`w-full text-left px-5 py-3.5 mb-3 bg-white text-gray-800 border-2 rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm hover:shadow-md ${isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-blue-400'}`}
        >
            {text}
        </button>
    );
  };

  // Skill level button with description
  const SkillLevelButton: React.FC<{title: string, description: string, field: Extract<SingleSelectFieldKey, 'skillLevel'>, selectedValue: string | null, onSelect: (field: Extract<SingleSelectFieldKey, 'skillLevel'>, value: string) => void}> = 
  ({ title, description, field, selectedValue, onSelect }) => {
    const isSelected = selectedValue === title;
    return (
        <button 
            onClick={() => onSelect(field, title)} 
            className={`w-full text-left px-5 py-3.5 mb-3 bg-white text-gray-800 border-2 rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm hover:shadow-md ${isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-blue-400'}`}
        >
            <span className="font-semibold block text-base">{title}</span>
            {description && <span className="text-sm text-gray-600 block mt-1">{description}</span>}
        </button>
    );
  };

  // Genre button (multi-select)
  const GenreButton: React.FC<{genre: string, selectedGenres: string[], onToggle: (genre: string) => void}> = 
  ({ genre, selectedGenres, onToggle }) => {
    const isSelected = selectedGenres.includes(genre);
    return (
        <button 
            onClick={() => onToggle(genre)} 
            className={`px-4 py-2.5 text-sm font-medium border-2 rounded-full transition-all duration-200 ease-in-out shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400 ${isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
        >
            {genre}
        </button>
    );
  };

  const SkipButton: React.FC<{onClick: () => void}> = ({onClick}) => (
    <button 
        onClick={onClick} 
        className="mt-6 text-sm text-center w-full text-blue-600 hover:text-blue-800 hover:underline transition-colors"
    >
        Skip this question
    </button>
);

  const progress = Math.round(((currentQuestionStep / TOTAL_QUESTIONS) * 100));

  return (
    <MinimalLayout>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col items-center justify-start pt-12 sm:pt-16 pb-20 px-4">
        <div className="w-full max-w-xl mx-auto">
          <div className="mb-6 flex items-center">
            {currentQuestionStep > 1 && (
              <button onClick={handlePreviousStep} className="text-blue-600 hover:text-blue-800 p-2 mr-2 rounded-full hover:bg-blue-100 transition-colors">
                <ChevronLeftIcon className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </button>
            )}
            <div className="flex-grow">
              {currentQuestionStep <= TOTAL_QUESTIONS && <ProgressBar />}
            </div>
          </div>
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-xl min-h-[350px] sm:min-h-[400px] flex flex-col justify-between">
            {renderQuestion()}
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default LearnerQuestionnairePage; 